import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { BaseLayout, UserProfile } from '@/components';
import { useAuth } from '../../hooks/use-auth';
import { readUser } from '../../api/user.api';
import { User } from '../../types/user.types';
import { useToast } from '../../contexts/toast-context';
import { TOAST_TYPES } from '../../constants/common.constants';
import { ROLES } from '../../constants/roles.constants';
import { useNavigate } from '@tanstack/react-router';
import { SkeletonLoader } from '@/components';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui';
import { signOut } from '../../utils/firebase';
import { cleanupFCMTokenForDevice } from '../../lib/fcm/init';
import userIsAuthorized from '../../utils/userIsAuthorized';
import { Button } from '../../components/ui';

export const Route = createFileRoute('/profile/')({
  component: Profile,
});

function Profile() {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [userObj, setUserObj] = useState<User | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const uuid = user?.uuid;
      if (uuid) {
        try {
          setIsLoading(true);
          const data = await readUser(uuid);
          setUserObj(data.user);
        } catch (error) {
          addToast(`Failed to fetch user profile`, {
            type: TOAST_TYPES.ERROR,
            title: 'Fetch Error',
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchUser();
  }, [user]);

  const handleLogout = async () => {
    try {
      // Before signOut: the token delete needs a logged-in user. Drivers keep theirs for reminders.
      if (userIsAuthorized(user?.role, [ROLES.MANAGER, ROLES.EXECUTIVE])) {
        await cleanupFCMTokenForDevice();
      }
      await signOut();
      addToast('Logged out successfully', { type: TOAST_TYPES.SUCCESS });
      navigate({ to: '/login', replace: true });
    } catch (err) {
      addToast('Failed to logout', { type: TOAST_TYPES.ERROR });
    }
  };

  return (
    <BaseLayout
      header={{
        headerText: 'Profile',
      }}
      breadcrumbs={{
        showBreadcrumbs: false,
      }}
    >
      {userObj && !isLoading ? (
        <Card>
          <CardHeader>
            <div className="flex w-full">
              <div className="flex-1">
                <CardTitle>{userObj.name}</CardTitle>
              </div>
              <div className="flex-1 flex justify-end gap-2">
                {/* Edit Button for Admins/ Execs/ Managers and Staff */}
                {(userObj.role === ROLES.ADMINISTRATOR ||
                  userObj.role === ROLES.EXECUTIVE ||
                  userObj.role === ROLES.MANAGER ||
                  userObj.role === ROLES.STAFF) && (
                  <Button
                    id="user-profile-edit-button"
                    type="button"
                    variant="outline-secondary"
                    size="small"
                    onClick={() =>
                      navigate({ to: `/organizations/${userObj.uuid}/edit` })
                    }
                  >
                    Edit
                  </Button>
                )}

                {/* Logout button for all users */}
                <Button
                  id="user-profile-logout-button"
                  type="button"
                  variant="outline-danger"
                  size="small"
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </div>
            </div>
          </CardHeader>
          <UserProfile user={userObj} />
        </Card>
      ) : (
        <Card>
          <CardContent>
            <SkeletonLoader count={10} height={30} />
          </CardContent>
        </Card>
      )}
    </BaseLayout>
  );
}
